// views/MainPage/RegisterPage/RegisterPage.tsx
import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { Order, Referenced, ReferralCode, Tier } from '../../../Entities';
import { deleteUrlParam, validateReferralCode } from '../../../utils/helpers';
import Loading from '../../../components/common/Loading/Loading';
import PaymentView from '../../../components/common/PaymentView/PaymentView';
import Modal from '../../../components/common/Modal/Modal';
import TextBox from '../../../components/common/TextBox/TextBox';
import InfoBadge from '../../../components/common/InfoBadge/InfoBadge';
import { notify } from '../../../components/common/Toast/toastBridge';
import styles from './RegisterPage.module.css';
import { initOnboardingTour } from '../../../components/common/OnboardingTour/OnboardingTour';

interface FormData {
  email: string;
  password: string;
  confirmPassword: string;
  nick: string;
  firstName: string;
  lastName: string;
  birthDate: string;
  selectedTier: Tier | null;
  termsAccepted: boolean;
  lostGuarantee: boolean;
  ageConfirmed: boolean;
  marketingConsent: boolean;
  isGoogleAuth: boolean;
  googleData: unknown;
  user: any;
}

const PASSWORD_RE = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z\d]).{8,}$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface PasswordCheck {
  key: string;
  label: string;
  met: boolean;
}

const getPasswordChecks = (password: string): PasswordCheck[] => [
  { key: 'length', label: 'Minimálně 8 znaků', met: password.length >= 8 },
  { key: 'case', label: 'Velké a malé písmeno', met: /[a-z]/.test(password) && /[A-Z]/.test(password) },
  { key: 'digit', label: 'Alespoň 1 číslo', met: /\d/.test(password) },
  { key: 'special', label: 'Speciální znak', met: /[^A-Za-z\d]/.test(password) },
];

const GoogleIcon = () => (
  <svg className={styles.googleIcon} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 533.5 544.3">
    <path fill="#4285F4" d="M533.5 278.4c0-17.6-1.6-34.5-4.6-50.9H272v95.9h146.9c-6.4 34.3-26 63.4-55.4 82.9v68h89.5c52.5-48.3 82.5-119.5 82.5-195.9z" />
    <path fill="#34A853" d="M272 544.3c74.2 0 136.6-24.6 182.1-66.8l-89.5-68c-24.8 16.7-56.5 26.5-92.6 26.5-71 0-131.2-48-152.8-112.7h-90v70.8c45.1 89 137.5 150.2 242.8 150.2z" />
    <path fill="#FBBC05" d="M119.2 323.3c-11.4-33.5-11.4-69.5 0-103l-90-70.8c-39 77.6-39 168.3 0 245.9l90-72.1z" />
    <path fill="#EA4335" d="M272 107.7c39.9 0 75.9 13.7 104.2 40.5l78-78C398.5 24.3 337.4 0 272 0 166.7 0 74.3 61.2 29.3 150.2l90 70.8c21.6-64.7 81.8-112.7 152.7-112.7z" />
  </svg>
);

const EmailIcon = () => (
  <svg className={styles.emailIcon} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none">
    <path d="M3.5 6.5h17a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1h-17a1 1 0 0 1-1-1v-9a1 1 0 0 1 1-1z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
    <path d="M3 7l9 6.5L21 7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const STEP_TITLES: Record<number, string> = { 1: 'Údaje', 2: 'Předplatné', 3: 'Platba' };

const RegisterPage = () => {
  const { onRegister, setRegError, user, regError } = useAuth();

  const [rememberMe, setRememberMe] = useState(false);
  const [hazardWarningModal, setHazardWarningModal] = useState(false);
  const [tiers, setTiers] = useState<Tier[] | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  const [currentStep, setCurrentStep] = useState<number | null>(null);
  const [preselectedTierId, setPreselectedTierId] = useState(2);
  const [referralCode, setReferralCode] = useState<ReferralCode | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [referencedSale] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(false);
  const [order, setOrder] = useState<Order | null>(null);

  const [formData, setFormData] = useState<FormData>({
    email: '',
    password: '',
    confirmPassword: '',
    nick: '',
    firstName: '',
    lastName: '',
    birthDate: '',
    selectedTier: null,
    termsAccepted: false,
    lostGuarantee: false,
    ageConfirmed: false,
    marketingConsent: false,
    isGoogleAuth: false,
    googleData: null,
    user,
  });

  // Initial setup from URL params / user state.
  const initialSetup = async (): Promise<void> => {
    window.scrollTo({ top: 0, behavior: 'smooth' });

    const params = new URLSearchParams(window.location.search);
    let step = 1;
    const redirectedFromPaygate: boolean = !!params.get('result');

    const tierId: number = Number(localStorage.getItem('tierId')) || 2;
    if (tierId) setPreselectedTierId(tierId);

    if (user) {
      if (redirectedFromPaygate) {
        step = 3;
      } else if (user.tier?.name?.toLowerCase() !== 'free') {
        navigate('/dashboard');
      } else {
        step = 2;
      }
      setCurrentStep(step);
      return;
    }

    validateReferralCode().then((referralCode: ReferralCode | null) => setReferralCode(referralCode));

    setCurrentStep(step)
  }
  useEffect(() => {
    initialSetup();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateFormData = <K extends keyof FormData>(field: K, value: FormData[K]) => {
    if (field === 'selectedTier' && value) {
      localStorage.setItem('tierId', String((value as Tier).id));
    }
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field as string]) {
      setErrors((prev) => ({ ...prev, [field as string]: '' }));
    }
  };

  const validateStep = (step: number | null): boolean => {
    const newErrors: Record<string, string> = {};

    if (step === 1) {
      if (!formData.isGoogleAuth) {
        if (!formData.email) newErrors.email = 'Email je povinný';
        else if (!EMAIL_RE.test(formData.email)) newErrors.email = 'Neplatný formát emailu';

        if (!formData.password) newErrors.password = 'Heslo je povinné';
        else if (formData.password.length < 8) newErrors.password = 'Heslo musí mít alespoň 8 znaků';
        else if (!PASSWORD_RE.test(formData.password))
          newErrors.password = 'Heslo musí mít alespoň 8 znaků, velké a malé písmeno, číslo a speciální znak.';

        if (formData.password !== formData.confirmPassword) newErrors.confirmPassword = 'Hesla se neshodují';
      }


      if (!formData.firstName) newErrors.firstName = 'Jméno je povinné';
      if (!formData.lastName) newErrors.lastName = 'Příjmení je povinné';

      if (!formData.nick) newErrors.nick = 'Přezdívka je povinná';
      else if (formData.nick.length < 3) newErrors.nick = 'Přezdívka musí mít alespoň 3 znaky';

      if (!formData.birthDate) newErrors.birthDate = 'Datum narození je povinné';
      else {
        const birthDate = new Date(formData.birthDate);
        const age = new Date().getFullYear() - birthDate.getFullYear();
        if (age < 18) newErrors.birthDate = 'Musíte být starší 18 let';
      }

      if (!formData.ageConfirmed) newErrors.ageConfirmed = 'Musíte potvrdit věk';
      if (!formData.termsAccepted) newErrors.termsAccepted = 'Musíte souhlasit s podmínkami';
      if (!formData.lostGuarantee) newErrors.lostGuarantee = 'Musíte souhlasit s podmínkami';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const getTiers = async () => {
    try {
      const all = await Tier.getAll();
      updateFormData('selectedTier', all[preselectedTierId - 1]);
      setTiers(all);
    } catch (error) {
      setRegError('Error while fetching Tiers');
      console.log(error);
    }
  };

  useEffect(() => {
    if (currentStep === 2) getTiers();
  }, [currentStep]);

  const handleRegistration = () => {
    setIsLoading(true);
    setRegError('');

    const birthTimestamp = new Date(formData.birthDate).getTime() / 1000;

    const registerPromise = formData.isGoogleAuth
      ? onRegister({
        method: 'google',
        firstName: formData.firstName,
        lastName: formData.lastName,
        name: formData.nick,
        birthTimestamp,
        rememberMe,
        referralCode: referralCode?.code,
      })
      : onRegister({
        method: 'email',
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        name: formData.nick,
        password: formData.password,
        birthTimestamp,
        rememberMe,
        referralCode: referralCode?.code,
      });

    // Not async/await: awaiting would block the Google popup window.
    registerPromise
      .then((newUser) => {
        updateFormData('user', newUser);
        initOnboardingTour(); // set the onboardingTour only after Registration
        handleNext();
      })
      .catch((error) => console.log(error))
      .finally(() => {
        setHazardWarningModal(false);
        setIsLoading(false);
      });
  };

  const handleOrderCreation = async () => {
    try {
      const created = await Order.create({
        tierId: Number(formData.selectedTier?.id),
        subscriptionScope: 'registration',
      });
      setOrder(created);
    } catch (error) {
      console.error(error);
      setRegError('Error while Order creation');
    }
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      if (currentStep === 1) navigate(location.pathname, { replace: true }); // drop referralCode after success
      setCurrentStep((prev) => (prev ?? 0) + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleBack = () => {
    setCurrentStep((prev) => (prev ?? 1) - 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (!currentStep) return <Loading variant="logo" />;

  if (hazardWarningModal) {
    return (
      <Modal isOpen onClose={() => setHazardWarningModal(false)} title="Rizika" size="medium">
        <div className={styles.warningModal}>
          <ul className={styles.warningList}>
            <li>žádná aktivita = žádný zisk</li>
            <li>princip výhody nad kasinem je legální, ale vždy je potřeba mít na paměti, že hazard je ze své podstaty ztrátový</li>
            <li>musíte přesně dodržovat <strong>VŠECHNY</strong> kroky, které od nás dostanete, abyste využili matematické výhody a předešli ztrátě</li>
            <li>pokud máte sklony k závislostem - NENÍ TO PRO VÁS, jakmile budete hrát podle emocí, o peníze příjdete!</li>
            <li>bacha na to</li>
          </ul>
          <div className={styles.actions}>
            <button className={styles.btnGhost} disabled={isLoading} onClick={() => setHazardWarningModal(false)}>
              ZRUŠIT
            </button>
            <button className={styles.btnPrimary} disabled={isLoading} onClick={handleRegistration}>
              Registrovat
            </button>
          </div>
        </div>
      </Modal>
    );
  }

  const renderStep1 = () => (
    <div className={styles.step}>
      <h2>Základní údaje</h2>
      <p className={styles.stepDescription}>Vyplňte základní informace pro vytvoření účtu</p>

      <div className={styles.authSwitch} role="tablist" aria-label="Způsob registrace">
        <span className={`${styles.authSwitchThumb} ${formData.isGoogleAuth ? styles.authSwitchThumbRight : ''}`} />
        <button
          type="button"
          role="tab"
          aria-selected={!formData.isGoogleAuth}
          className={`${styles.authSwitchOption} ${!formData.isGoogleAuth ? styles.authSwitchActive : ''}`}
          onClick={() => setFormData((prev) => ({ ...prev, isGoogleAuth: false }))}
        >
          <EmailIcon /> Email
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={formData.isGoogleAuth}
          className={`${styles.authSwitchOption} ${formData.isGoogleAuth ? styles.authSwitchActive : ''}`}
          onClick={() => setFormData((prev) => ({ ...prev, isGoogleAuth: true }))}
        >
          <GoogleIcon /> Google
        </button>
      </div>

      <div className={styles.formGroup}>
        <label htmlFor="nick">Přezdívka *</label>
        <input id="nick" type="text" className={`${styles.input} ${errors.nick ? styles.inputError : ''}`}
          value={formData.nick} onChange={(e) => updateFormData('nick', e.target.value)} placeholder="VašeJméno123" />
        {errors.nick && <span className={styles.errorText}>{errors.nick}</span>}
      </div>

      <div className={styles.formGroup}>
        <label htmlFor="firstName">Křestní jméno *</label>
        <input id="firstName" type="text" className={`${styles.input} ${errors.firstName ? styles.inputError : ''}`}
          value={formData.firstName} onChange={(e) => updateFormData('firstName', e.target.value)} placeholder="Jan" />
        {errors.firstName && <span className={styles.errorText}>{errors.firstName}</span>}
      </div>

      <div className={styles.formGroup}>
        <label htmlFor="lastName">Příjmení *</label>
        <input id="lastName" type="text" className={`${styles.input} ${errors.lastName ? styles.inputError : ''}`}
          value={formData.lastName} onChange={(e) => updateFormData('lastName', e.target.value)} placeholder="Novák" />
        {errors.lastName && <span className={styles.errorText}>{errors.lastName}</span>}
      </div>

      <div className={styles.formGroup}>
        <label htmlFor="birthDate">Datum narození *</label>
        <input id="birthDate" type="date" className={`${styles.input} ${errors.birthDate ? styles.inputError : ''}`}
          value={formData.birthDate} onChange={(e) => updateFormData('birthDate', e.target.value)} />
        {errors.birthDate && <span className={styles.errorText}>{errors.birthDate}</span>}
        <small className={styles.ageWarning}>⚠️ Platforma není vhodná pro osoby mladší 18 let</small>
      </div>

      {!formData.isGoogleAuth && (
        <>
          <div className={styles.formGroup}>
            <label htmlFor="email">Email *</label>
            <input id="email" type="email" className={`${styles.input} ${errors.email ? styles.inputError : ''}`}
              value={formData.email} onChange={(e) => updateFormData('email', e.target.value)} placeholder="vas@email.cz" />
            {errors.email && <span className={styles.errorText}>{errors.email}</span>}
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="password">Heslo *</label>
            <input id="password" type="password" className={`${styles.input} ${errors.password ? styles.inputError : ''}`}
              value={formData.password} onChange={(e) => updateFormData('password', e.target.value)} placeholder="••••••••" />
            {errors.password && <span className={styles.errorText}>{errors.password}</span>}
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="confirmPassword">Potvrzení hesla *</label>
            <input id="confirmPassword" type="password" className={`${styles.input} ${errors.confirmPassword ? styles.inputError : ''}`}
              value={formData.confirmPassword} onChange={(e) => updateFormData('confirmPassword', e.target.value)} placeholder="••••••••" />
            {errors.confirmPassword && <span className={styles.errorText}>{errors.confirmPassword}</span>}
          </div>

          <div className={styles.requirements}>
            <h4>Požadavky na heslo:</h4>
            <ul>
              {getPasswordChecks(formData.password).map((check) => (
                <li key={check.key} className={check.met ? styles.reqMet : styles.reqUnmet}>
                  <span className={styles.reqIcon}>{check.met ? '✓' : '•'}</span>
                  {check.label}
                </li>
              ))}
            </ul>
          </div>
        </>
      )}

      <label className={styles.checkbox}>
        <input type="checkbox" checked={formData.ageConfirmed} onChange={(e) => updateFormData('ageConfirmed', e.target.checked)} />
        <span>Potvrzuji, že mi je více než 18 let *</span>
      </label>
      {errors.ageConfirmed && <span className={`${styles.errorText} ${styles.checkboxErrorText}`}>{errors.ageConfirmed}</span>}

      <label className={styles.checkbox}>
        <input type="checkbox" checked={formData.termsAccepted} onChange={(e) => updateFormData('termsAccepted', e.target.checked)} />
        <span>
          Souhlasím s{' '}
          <a href="https://src.algapia.cz/docs/VOP.pdf" target="_blank" rel="noopener noreferrer">obchodními podmínkami</a> *
        </span>
      </label>
      {errors.termsAccepted && <span className={`${styles.errorText} ${styles.checkboxErrorText}`}>{errors.termsAccepted}</span>}

      <label className={styles.checkbox}>
        <input type="checkbox" checked={formData.lostGuarantee} onChange={(e) => updateFormData('lostGuarantee', e.target.checked)} />
        <span>
          Souhlasím s okamžitým dodáním digitálního obsahu a vím, že tímto ztrácím právo odstoupit od smlouvy podle § 1837 písm. l) NOZ.*
          <InfoBadge title="Info" footer="Děkujeme za pochopení">
            <p>Chci digitální obsah zpřístupnit okamžitě: hned po registraci získám přístup ke strategiím, bonusům a do klubové sekce, které mohu rovnou začít využívat.</p>
            <p>Rozumím tomu, že jakmile tyto materiály uvidím, nebylo by fér aplikovat obecné zákonné právo na odstoupení do 14 dní při nákupu na dálku. Proto souhlasím s tím, že toto právo na odstoupení podle § 1837 písm. l) NOZ ztrácím. Toto je běžná a zákonná praxe u digitálních produktů, které chci využívat okamžitě.</p>
            <p>Beru na vědomí, že v případě Basic členství nemusím při registraci nic platit – pokud by se mi produkt nelíbil, mám 30 dní na vyzkoušení zdarma.</p>
          </InfoBadge>
        </span>
      </label>
      {errors.lostGuarantee && <span className={`${styles.errorText} ${styles.checkboxErrorText}`}>{errors.lostGuarantee}</span>}

      {formData.isGoogleAuth && (
        <button className={styles.googleBtn} disabled={isLoading}
          onClick={() => { if (validateStep(currentStep)) setHazardWarningModal(true); }}>
          <GoogleIcon /> Register with Google
        </button>
      )}
    </div>
  );

  const renderStep2 = () => {
    if (!tiers || !formData.selectedTier) return <Loading />;

    const selected = formData.selectedTier;
    const usedFreeTrial = Boolean(formData.user?.used_free_trial);

    return (
      <div className={styles.step}>
        <h2>Výběr předplatného</h2>
        <p className={styles.stepDescription}>Vyberte si předplatné, které nejlépe vyhovuje vašim potřebám</p>

        <div className={styles.membershipGrid}>
          {tiers.filter((t) => t.in_use).map((tier) => (
            <div
              key={tier.name}
              className={`${styles.membershipCard} ${selected.name === tier.name ? styles.selected : ''}`}
              onClick={() => updateFormData('selectedTier', tier)}
            >
              <div className={styles.membershipHeader}>
                <h3>{tier.name}</h3>
                <div className={styles.price}>
                  {tier.price === 0 ? (
                    <span className={styles.free}>Zdarma</span>
                  ) : !usedFreeTrial && tier.free_trial_days !== 0 ? (
                    <span>
                      <del>{tier.price} Kč/měsíc</del>{' '}
                      <span className={styles.trialTag}><i>{tier.free_trial_days} denní free trial</i></span>
                    </span>
                  ) : (
                    <span>{tier.price} Kč/měsíc</span>
                  )}
                </div>
              </div>
              <ul className={styles.membershipFeatures}>
                {(tier.features as string[]).map((feature) => (
                  <li key={feature}>{feature}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {selected.name?.toLowerCase() !== 'free' && (
          <div className={styles.priceOverview}>
            <div className={styles.priceItem}>
              <span>Základní cena:</span>
              <span>{selected.price} Kč</span>
            </div>
            {!usedFreeTrial && (
              <div className={styles.priceItem}>
                <span>Free trial</span>
                <span className={styles.discount}>-{selected.price} Kč</span>
              </div>
            )}
            <div className={`${styles.priceItem} ${styles.total}`}>
              <span>Celkem k zaplacení:</span>
              <span className={styles.initPrice}>
                {!usedFreeTrial && selected.free_trial_days !== 0 ? (
                  <>Zdarma na {selected.free_trial_days} dny/dní, poté {selected.price} Kč/měsíc</>
                ) : (
                  <>{Math.max(0, (selected.price ?? 0) - referencedSale)} Kč/měsíc</>
                )}
              </span>
            </div>
          </div>
        )}

        {!usedFreeTrial && (
          <TextBox mode="tip">
            <div className={styles.freeTrialTip}>
              <p>V dalším kroku je vyžadováno potvrzení platební kartou pro ověření identity. Nic vám účtováno nebude.</p>
              <p>Pokud během 30 dní zjistíte, že to není pro vás, členství jednoduše zrušte – zdarma, bez podmínek.</p>
              <p>Doporučujeme nastavit si připomínku na 25. den. Zrušení je možné kdykoliv v Nastavení → Členství.</p>
            </div>
          </TextBox>
        )}
      </div>
    );
  };

  const selectedIsFree = formData.selectedTier?.name?.toLowerCase() === 'free';

  return (
    <div className={styles.registerPage}>
      <div className={styles.container}>
        <div className={styles.card}>
          <div className={styles.header}>
            <h1>Registrace</h1>
            <p className={styles.subtitle}>Připojte se k DigiBrigi a začněte vydělávat</p>
          </div>

          {/* Step indicator */}
          <div className={styles.stepIndicator}>
            <div className={styles.stepDots}>
              {[1, 2, 3].map((step) => (
                <div key={step} className={`${styles.stepDot} ${step <= (currentStep ?? 0) ? styles.active : ''} ${step < (currentStep ?? 0) ? styles.completed : ''}`}>
                  {step}
                </div>
              ))}
            </div>
            <div className={styles.stepTitles}>
              {[1, 2, 3].map((step) => (
                <div key={step} className={`${styles.stepTitle} ${step <= (currentStep ?? 0) ? styles.active : ''}`}>
                  {STEP_TITLES[step]}
                </div>
              ))}
            </div>
            {regError && <div className={styles.error}>{regError}</div>}
          </div>

          <div className={styles.body}>
            {currentStep === 1 && renderStep1()}
            {currentStep === 2 && renderStep2()}
            {currentStep === 3 && (
              <PaymentView
                order={order}
                formData={formData}
                title="Dokončení registrace"
                subtitle="Nyní dokončíme platbu a aktivujeme váš účet"
              />
            )}

            {currentStep! < 3 && (
              <div className={styles.formActions}>
                {currentStep! > 1 && (
                  <button className={styles.btnGhost} onClick={handleBack}>Zpět</button>
                )}

                {currentStep === 1 && (
                  <label className={styles.remember}>
                    <input checked={rememberMe} type="checkbox" onChange={() => setRememberMe((v) => !v)} />
                    <span>Zapamatovat si mě</span>
                  </label>
                )}

                {currentStep! < 2 ? (
                  !formData.isGoogleAuth && (
                    <button className={styles.btnPrimary} disabled={isLoading}
                      onClick={() => { if (validateStep(currentStep)) setHazardWarningModal(true); }}>
                      Pokračovat
                    </button>
                  )
                ) : (
                  <button className={styles.btnPrimary} disabled={!formData.selectedTier}
                    onClick={() => {
                      if (selectedIsFree) {
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                        navigate('/dashboard');
                      } else {
                        handleOrderCreation();
                        setCurrentStep(3);
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }
                    }}>
                    {selectedIsFree ? 'Registrovat a přejít na Dashboard' : 'Pokračovat k platbě'}
                  </button>
                )}
              </div>
            )}
          </div>

          {currentStep === 1 && (
            <div className={styles.loginLink}>
              <p>Již máte účet? <Link to="/login">Přihlásit se</Link></p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;